import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from './pages/LoginPage'
import { AppPage } from './pages/AppPage'
import { ToastContainer } from './components/ui/Toast'
import { MediaViewer } from './components/ui/MediaViewer'
import { QuickSwitcher } from './components/ui/QuickSwitcher'
import { GlobalSearch } from './components/ui/GlobalSearch'
import { ShortcutsModal } from './components/ui/ShortcutsModal'
import { UpdateBanner } from './components/ui/UpdateBanner'
import { useAuthStore } from './stores/authStore'
import { useChatStore } from './stores/chatStore'
import { useUiStore } from './stores/uiStore'
import { useVersionCheck } from './hooks/useVersionCheck'
import { useTabGuard } from './hooks/useTabGuard'
import { usePushNotifications } from './hooks/usePushNotifications'
import { useScheduledMessages } from './hooks/useScheduledMessages'
import { tryRefreshToken } from './api/client'

// Refresh when less than 48 h remain on a 3-day token → fires once per day
const REFRESH_AHEAD_MS = 48 * 60 * 60 * 1000

function jwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function needsRefresh(token: string | null): boolean {
  if (!token) return true
  const exp = jwtExp(token)
  if (exp === null) return true
  return exp - REFRESH_AHEAD_MS < Date.now()
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [ready, setReady] = useState(false)
  const [authed, setAuthed] = useState(false)

  useEffect(() => {
    const access = localStorage.getItem('access_token')
    const refresh = localStorage.getItem('refresh_token')

    if (!access && !refresh) {
      setAuthed(false)
      setReady(true)
      return
    }

    if (!needsRefresh(access)) {
      setAuthed(true)
      setReady(true)
    } else {
      // Expired or expiring within 24 h — refresh before rendering
      tryRefreshToken().then((ok) => {
        setAuthed(ok || isAuthenticated)
        setReady(true)
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Background proactive refresh: check every 15 min + on tab focus.
  // Browsers throttle setInterval in background tabs, so the hourly tick
  // can be skipped entirely during sleep/background — visibilitychange
  // catches the "return to tab" moment before the first request fires.
  useEffect(() => {
    if (!authed) return
    const check = () => {
      const token = localStorage.getItem('access_token')
      if (needsRefresh(token)) tryRefreshToken()
    }
    const id = setInterval(check, 15 * 60 * 1000) // every 15 min
    const onVisible = () => { if (!document.hidden) check() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [authed])

  if (!ready) return null
  if (!authed) return <Navigate to="/login" replace />
  return <>{children}</>
}

export function App() {
  // Синхронизируем высоту shell с visualViewport.height.
  // Это исправляет два бага на мобильных браузерах:
  // 1. Клавиатура перекрывает инпут (viewport не сжимается автоматически)
  // 2. Хидер "уезжает" вверх из-за iOS auto-scroll при фокусе на textarea
  useEffect(() => {
    function update() {
      const h = window.visualViewport?.height ?? window.innerHeight
      document.documentElement.style.setProperty('--actual-height', `${h}px`)
    }
    update()
    window.visualViewport?.addEventListener('resize', update)
    window.addEventListener('resize', update)
    return () => {
      window.visualViewport?.removeEventListener('resize', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  const unread = useChatStore((s) => s.unread)
  const { globalSearchOpen, setGlobalSearchOpen } = useUiStore()
  const [showQuickSwitcher, setShowQuickSwitcher] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const updateAvailable = useVersionCheck()
  const multiTab = useTabGuard()
  usePushNotifications()
  useScheduledMessages()
  const [installPrompt, setInstallPrompt] = useState<Event & { prompt?: () => void } | null>(null)

  useEffect(() => {
    const handler = (e: Event) => { e.preventDefault(); setInstallPrompt(e as Event & { prompt?: () => void }) }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  // Update document title and favicon badge based on total unread count
  useEffect(() => {
    const total = Object.values(unread).reduce((sum, n) => sum + n, 0)
    document.title = total > 0 ? `(${total}) GGRAM` : 'GGRAM'

    // Dynamic favicon badge
    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 32
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Draw base icon (filled circle)
    ctx.fillStyle = '#5B8DEF'
    ctx.beginPath()
    ctx.arc(16, 16, 14, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#fff'
    ctx.font = 'bold 16px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('G', 16, 17)

    if (total > 0) {
      // Red badge
      ctx.fillStyle = '#EF4444'
      ctx.beginPath()
      ctx.arc(24, 8, 8, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 9px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(total > 99 ? '99' : String(total), 24, 8)
    }

    const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (link) {
      link.href = canvas.toDataURL('image/png')
    }
  }, [unread])

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl+K — quick switcher
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setShowQuickSwitcher((s) => !s)
      }

      // Ctrl+Shift+F — global search
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'F') {
        e.preventDefault()
        setGlobalSearchOpen(!globalSearchOpen)
      }

      // Ctrl+/ — keyboard shortcuts guide
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault()
        setShowShortcuts((s) => !s)
      }

      // Esc — close modals
      if (e.key === 'Escape') {
        setShowShortcuts(false)
        setGlobalSearchOpen(false)
      }

      // Alt+← / Alt+→ — navigate between chats
      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault()
        const { chats, activeChatId, setActiveChat } = useChatStore.getState()
        const idx = chats.findIndex((c) => c.id === activeChatId)
        if (e.key === 'ArrowLeft' && idx > 0) setActiveChat(chats[idx - 1].id)
        if (e.key === 'ArrowRight' && idx < chats.length - 1) setActiveChat(chats[idx + 1].id)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ToastContainer />
      <MediaViewer />
      {showQuickSwitcher && <QuickSwitcher onClose={() => setShowQuickSwitcher(false)} />}
      {globalSearchOpen && <GlobalSearch onClose={() => setGlobalSearchOpen(false)} />}
      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
      {updateAvailable && <UpdateBanner />}
      {installPrompt && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-bg-primary border border-divider rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 animate-fade-in max-w-sm w-full mx-4">
          <span className="text-2xl">📱</span>
          <div className="flex-1 min-w-0">
            <p className="text-txt-primary text-sm font-medium">Install GGRAM</p>
            <p className="text-txt-meta text-xs">Add to home screen for the best experience</p>
          </div>
          <button
            onClick={() => { installPrompt.prompt?.(); setInstallPrompt(null) }}
            className="bg-accent text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-blue-500 transition-colors shrink-0"
          >
            Install
          </button>
          <button onClick={() => setInstallPrompt(null)} className="text-txt-meta hover:text-txt-secondary text-sm shrink-0">✕</button>
        </div>
      )}
      {multiTab && (
        <div className="fixed top-0 inset-x-0 z-[101] bg-yellow-500/90 text-black text-center text-sm py-2 font-medium">
          ⚠️ GGRAM is open in another tab — some features may not work correctly
          <button onClick={() => window.location.reload()} className="ml-3 underline">Reload</button>
        </div>
      )}
    </BrowserRouter>
  )
}
